import React, { useState, useEffect, useCallback } from 'react';
import { API, formatWeight, formatDateTime, displayDate, todayIST } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Filter, Download, ChevronDown, ChevronUp, BarChart3 } from 'lucide-react';

function SummaryTab() {
  const [summaryData, setSummaryData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState(todayIST());
  const [endDate, setEndDate] = useState(todayIST());
  const [expandedCat, setExpandedCat] = useState(null);
  const [expandedName, setExpandedName] = useState(null);

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const res = await API.get('/reports/daily-summary', { params });
      setSummaryData(res.data);
      setExpandedCat(null);
      setExpandedName(null);
    } catch { toast.error('Failed to load summary'); }
    finally { setLoading(false); }
  }, [startDate, endDate]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  const typeColors = {
    sale: 'text-emerald-400', sale_return: 'text-rose-400',
    purchase: 'text-blue-400', purchase_return: 'text-amber-400',
    add_poly: 'text-teal-400', subtract_poly: 'text-orange-400',
  };

  const toggleCat = (type) => { setExpandedCat(prev => prev === type ? null : type); setExpandedName(null); };
  const toggleName = (key) => setExpandedName(prev => prev === key ? null : key);

  const grandTotal = summaryData.reduce((s, c) => {
    if (['sale', 'purchase', 'add_poly'].includes(c.type)) return s + c.total_g;
    return s - c.total_g;
  }, 0);

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="pt-4 pb-3">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-end gap-3">
            <div className="flex-1">
              <label className="text-xs font-medium mb-1 block">Start Date</label>
              <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} data-testid="summary-start-date" />
            </div>
            <div className="flex-1">
              <label className="text-xs font-medium mb-1 block">End Date</label>
              <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} data-testid="summary-end-date" />
            </div>
            <Button onClick={fetchSummary} disabled={loading} className="sm:w-auto" data-testid="summary-apply-btn">
              <BarChart3 size={14} className="mr-1" />{loading ? 'Loading...' : 'Apply'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        {summaryData.map(cat => (
          <Card key={cat.type} className="cursor-pointer hover:ring-1 hover:ring-primary/30 transition-all" onClick={() => toggleCat(cat.type)}
            data-testid={`summary-card-${cat.type}`}>
            <CardContent className="pt-4 px-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">{cat.label}</p>
                  <p className={`text-xl font-bold font-mono ${typeColors[cat.type] || ''}`}>{cat.total_g == null ? '—' : formatWeight(cat.total_g)}</p>
                  <p className="text-[10px] text-muted-foreground">{cat.count} entries</p>
                </div>
                {expandedCat === cat.type ? <ChevronUp size={16} className="text-muted-foreground" /> : <ChevronDown size={16} className="text-muted-foreground" />}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Expanded Category Detail */}
      {expandedCat && (() => {
        const cat = summaryData.find(c => c.type === expandedCat);
        if (!cat || cat.by_name.length === 0) return (
          <Card><CardContent className="py-6 text-center text-sm text-muted-foreground">No entries</CardContent></Card>
        );
        return (
          <Card data-testid={`summary-detail-${expandedCat}`}>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <span className={typeColors[expandedCat]}>{cat.label}</span>
                <span className="text-xs text-muted-foreground font-normal">— {cat.by_name.length} {['sale', 'sale_return'].includes(expandedCat) ? 'customers' : ['purchase', 'purchase_return'].includes(expandedCat) ? 'suppliers' : 'entries'}</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-1">
                {cat.by_name.map((entry, idx) => {
                  const nameKey = `${expandedCat}-${idx}`;
                  const isNameExpanded = expandedName === nameKey;
                  return (
                    <div key={nameKey}>
                      <div className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50 cursor-pointer" onClick={() => toggleName(nameKey)}
                        data-testid={`summary-name-${expandedCat}-${idx}`}>
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="font-semibold text-sm truncate">{entry.name}</span>
                          <span className="text-xs text-muted-foreground">({entry.count})</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`font-mono font-semibold text-sm ${typeColors[expandedCat]}`}>{formatWeight(entry.total_g)}</span>
                          {isNameExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                        </div>
                      </div>
                      {isNameExpanded && entry.transactions && (
                        <div className="ml-4 mb-2 border-l-2 border-border/40 pl-3">
                          <Table>
                            <TableHeader><TableRow>
                              <TableHead className="text-xs">Date</TableHead>
                              <TableHead className="text-xs">Box</TableHead>
                              <TableHead className="text-xs text-right">Weight (g)</TableHead>
                            </TableRow></TableHeader>
                            <TableBody>
                              {entry.transactions.map((t, ti) => (
                                <TableRow key={ti}>
                                  <TableCell className="text-xs">{t.date}</TableCell>
                                  <TableCell className="text-xs font-mono">{t.box_id}</TableCell>
                                  <TableCell className="text-xs text-right font-mono">{formatWeight(t.gold_weight_g)}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        );
      })()}
    </div>
  );
}

export default function ReportsPage() {
  const [reportType, setReportType] = useState('summary');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [txnType, setTxnType] = useState('all');
  const [itemFilter, setItemFilter] = useState('');
  const [items, setItems] = useState([]);
  const [expandedRow, setExpandedRow] = useState(null);
  const [page, setPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const pageSize = 50;

  useEffect(() => {
    API.get('/items').then(r => setItems(r.data)).catch(() => {});
  }, []);

  const fetchReport = async () => {
    if (reportType === 'summary') return; // SummaryTab handles its own data
    setLoading(true);
    try {
      const endpoint = reportType === 'customer' ? '/reports/customer-wise' : '/reports/karigar-wise';
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (txnType !== 'all') params.transaction_type = txnType;
      if (itemFilter) params.item_name = itemFilter;
      params.page = page;
      params.page_size = pageSize;
      const res = await API.get(endpoint, { params });
      const result = res.data;
      setData(result.data || []);
      setTotalRecords(result.total || 0);
      setExpandedRow(null);
    } catch (e) { toast.error('Failed to load report'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchReport(); }, [reportType, page]);

  const txnTypeOptions = reportType === 'customer'
    ? [{ v: 'all', l: 'All' }, { v: 'sale', l: 'Sale' }, { v: 'sale_return', l: 'Sale Return' }]
    : [{ v: 'all', l: 'All' }, { v: 'purchase', l: 'Purchase' }, { v: 'purchase_return', l: 'P. Return' }];

  const toggleExpand = (idx) => setExpandedRow(prev => prev === idx ? null : idx);

  return (
    <div data-testid="reports-page">
      <div className="page-header">
        <h1>Reports</h1>
        <p className="hidden sm:block">Daily summary, customer-wise & karigar-wise reports</p>
      </div>

      <Tabs value={reportType} onValueChange={v => { setReportType(v); setPage(1); }}>
        <TabsList className="mb-4 w-full grid grid-cols-3">
          <TabsTrigger value="summary" data-testid="report-tab-summary">Summary</TabsTrigger>
          <TabsTrigger value="customer" data-testid="report-tab-customer">Customer Wise</TabsTrigger>
          <TabsTrigger value="karigar" data-testid="report-tab-karigar">Karigar Wise</TabsTrigger>
        </TabsList>
      </Tabs>

      {reportType === 'summary' && <SummaryTab />}

      {reportType !== 'summary' && (
        <>
          {/* Filters */}
          <Card className="mb-4">
            <CardContent className="pt-4 pb-3">
              <div className="flex flex-col sm:flex-row items-stretch sm:items-end gap-3">
                <div className="flex-1">
                  <label className="text-xs font-medium mb-1 block">Start Date</label>
                  <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} data-testid="report-start-date" />
                </div>
                <div className="flex-1">
                  <label className="text-xs font-medium mb-1 block">End Date</label>
                  <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} data-testid="report-end-date" />
                </div>
                <div className="flex-1">
                  <label className="text-xs font-medium mb-1 block">Transaction Type</label>
                  <Select value={txnType} onValueChange={setTxnType}>
                    <SelectTrigger data-testid="report-txn-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {txnTypeOptions.map(o => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex-1">
                  <label className="text-xs font-medium mb-1 block">Item Name</label>
                  <Select value={itemFilter || '_all'} onValueChange={v => setItemFilter(v === '_all' ? '' : v)}>
                    <SelectTrigger data-testid="report-item-filter"><SelectValue placeholder="All items" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_all">All Items</SelectItem>
                      {items.map(i => <SelectItem key={i.id} value={i.name}>{i.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={() => { setPage(1); fetchReport(); }} disabled={loading} className="sm:w-auto" data-testid="report-apply-btn">
                  <Filter size={14} className="mr-1" />{loading ? 'Loading...' : 'Apply'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Results */}
          {data.length === 0 && !loading && (
            <p className="text-center py-8 text-sm text-muted-foreground">No data found for the selected filters.</p>
          )}

          {/* Mobile card view */}
          <div className="mobile-card-list space-y-3">
            {data.map((row, idx) => {
              const name = reportType === 'customer' ? row.customer_name : row.karigar_name;
              const isExpanded = expandedRow === idx;
              return (
                <Card key={idx} data-testid={`report-row-${idx}`}>
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between cursor-pointer" onClick={() => toggleExpand(idx)}>
                      <div>
                        <p className="font-semibold text-sm">{name}</p>
                        {reportType === 'customer' && row.customer_mobile && <p className="text-xs text-muted-foreground font-mono">{row.customer_mobile}</p>}
                        <p className="text-xs text-muted-foreground">{row.transaction_count} transactions</p>
                      </div>
                      <div className="text-right">
                        <div className="flex gap-2 mb-1">
                          {['76', '84', '92'].map(p => row.totals[p] !== 0 && (
                            <span key={p} className={`text-xs px-1.5 py-0.5 rounded status-badge purity-${p} font-mono`}>{row.totals[p]}g</span>
                          ))}
                        </div>
                        <p className="text-xs font-mono font-bold" style={{ color: 'hsl(43, 96%, 56%)' }}>Total: {row.totals.total_gold_g}g</p>
                        {isExpanded ? <ChevronUp size={14} className="inline ml-1" /> : <ChevronDown size={14} className="inline ml-1" />}
                      </div>
                    </div>
                    {isExpanded && row.transactions && row.transactions.length > 0 && (
                      <div className="mt-3 border-t pt-2">
                        <Table>
                          <TableHeader><TableRow>
                            <TableHead className="text-xs">Date</TableHead>
                            <TableHead className="text-xs">Type</TableHead>
                            <TableHead className="text-xs">Box</TableHead>
                            <TableHead className="text-xs">Purity</TableHead>
                            <TableHead className="text-xs text-right">Weight</TableHead>
                          </TableRow></TableHeader>
                          <TableBody>
                            {row.transactions.map((t, ti) => (
                              <TableRow key={ti}>
                                <TableCell className="text-xs">{t.date ? (typeof t.date === 'string' && t.date.includes('T') ? formatDateTime(t.date) : t.date) : '-'}</TableCell>
                                <TableCell className="text-xs capitalize">{(t.type || '').replace(/_/g, ' ')}</TableCell>
                                <TableCell className="text-xs font-mono">{t.box_id}</TableCell>
                                <TableCell><span className={`status-badge purity-${t.purity} text-xs`}>{t.purity}</span></TableCell>
                                <TableCell className="text-xs text-right font-mono weight-value">{formatWeight(t.gold_weight_g)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Desktop table view */}
          <div className="desktop-table">
            <Card><CardContent className="p-0"><div className="table-scroll-wrapper"><Table className="data-table">
              <TableHeader><TableRow>
                <TableHead>{reportType === 'customer' ? 'Customer' : 'Karigar'}</TableHead>
                {reportType === 'customer' && <TableHead>Mobile</TableHead>}
                <TableHead className="text-right">76 (g)</TableHead>
                <TableHead className="text-right">84 (g)</TableHead>
                <TableHead className="text-right">92 (g)</TableHead>
                <TableHead className="text-right">Total Gold (g)</TableHead>
                <TableHead className="text-right">Txns</TableHead>
                <TableHead></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {data.map((row, idx) => {
                  const name = reportType === 'customer' ? row.customer_name : row.karigar_name;
                  const isExpanded = expandedRow === idx;
                  return (
                    <React.Fragment key={idx}>
                      <TableRow className="cursor-pointer hover:bg-muted/50" onClick={() => toggleExpand(idx)} data-testid={`report-desk-row-${idx}`}>
                        <TableCell className="font-semibold">{name}</TableCell>
                        {reportType === 'customer' && <TableCell className="font-mono text-sm">{row.customer_mobile}</TableCell>}
                        <TableCell className="text-right weight-value">{formatWeight(row.totals['76'])}</TableCell>
                        <TableCell className="text-right weight-value">{formatWeight(row.totals['84'])}</TableCell>
                        <TableCell className="text-right weight-value">{formatWeight(row.totals['92'])}</TableCell>
                        <TableCell className="text-right font-mono font-bold" style={{ color: 'hsl(43, 96%, 56%)' }}>{formatWeight(row.totals.total_gold_g)}</TableCell>
                        <TableCell className="text-right">{row.transaction_count}</TableCell>
                        <TableCell>{isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}</TableCell>
                      </TableRow>
                      {isExpanded && (
                        <TableRow key={`${idx}-detail`}>
                          <TableCell colSpan={reportType === 'customer' ? 8 : 7} className="p-0">
                            <div className="p-3 bg-muted/20">
                              <Table>
                                <TableHeader><TableRow>
                                  <TableHead className="text-xs">Date</TableHead>
                                  <TableHead className="text-xs">Type</TableHead>
                                  <TableHead className="text-xs">Box</TableHead>
                                  <TableHead className="text-xs">Purity</TableHead>
                                  <TableHead className="text-xs text-right">Weight (g)</TableHead>
                                  {reportType === 'customer' && <TableHead className="text-xs">Bill #</TableHead>}
                                </TableRow></TableHeader>
                                <TableBody>
                                  {(row.transactions || []).map((t, ti) => (
                                    <TableRow key={ti}>
                                      <TableCell className="text-xs">{t.date ? (typeof t.date === 'string' && t.date.includes('T') ? formatDateTime(t.date) : t.date) : '-'}</TableCell>
                                      <TableCell className="text-xs capitalize">{(t.type || '').replace(/_/g, ' ')}</TableCell>
                                      <TableCell className="text-xs font-mono">{t.box_id}</TableCell>
                                      <TableCell><span className={`status-badge purity-${t.purity} text-xs`}>{t.purity}</span></TableCell>
                                      <TableCell className="text-xs text-right font-mono weight-value">{formatWeight(t.gold_weight_g)}</TableCell>
                                      {reportType === 'customer' && <TableCell className="text-xs font-mono">{t.bill_number || '-'}</TableCell>}
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
                {data.length === 0 && <TableRow><TableCell colSpan={reportType === 'customer' ? 8 : 7} className="text-center py-8 text-muted-foreground">No data found</TableCell></TableRow>}
              </TableBody>
            </Table></div></CardContent></Card>
          </div>

          {/* Pagination */}
          {totalRecords > pageSize && (
            <div className="flex items-center justify-between mt-4 px-1" data-testid="report-pagination">
              <p className="text-xs text-muted-foreground">
                Showing {Math.min((page - 1) * pageSize + 1, totalRecords)}-{Math.min(page * pageSize, totalRecords)} of {totalRecords}
              </p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="report-prev-page">Previous</Button>
                <Button variant="outline" size="sm" disabled={page * pageSize >= totalRecords} onClick={() => setPage(p => p + 1)} data-testid="report-next-page">Next</Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
